import { Button, Checkbox, Collapse, Input, Modal, Table } from "antd";
import { api } from "../../../../config";
import "./style.css";
import { useState } from "react";
import { useSelector } from "react-redux";
import { AuthenticationState } from "../../../../interfaces/users";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import { FieldReportViewModel } from "../../../../interfaces";
import { formatCurrency, truncateString } from "../../../../utils";
import { SearchAssemblers } from "../../../Shared/Components/SearchAssemblers/SearchAssemblers";
import { ModulePermission, userHasPermission } from "../../../../utils/helpers/userHelpers";

dayjs.extend(duration);

interface PaymentProps {
  refetch?: any;
  isOpen: boolean;
  onClose: () => void;
  onOpen: () => void;
}

export const AdditionsApprovementModal: React.FC<PaymentProps> = ({
  refetch,
  isOpen,
  onClose,
  onOpen,
}) => {
  const { token, user } = useSelector((state: AuthenticationState) => state);
  const [searchField, setSearchField] = useState("");
  const [selectedTickets, setSelectedTickets] = useState<number[]>([]);

  const [allTickets, setAllTickets] = useState<FieldReportViewModel[]>([]);
  const [assembler, setAssembler] = useState<number | undefined>();

  const [loading, setLoading] = useState(false);

  const handleSearch = async () => {
    try {
      setLoading(true);
      const response = await api(token).get(
        `tickets/assemble/?${
          assembler ? `&ownerId=${assembler}` : ""
        }&forApprovement=1&pageSize=10000${
          searchField ? `&search=${searchField}` : ""
        }`
      );

      setAllTickets(response.tickets);
      setSelectedTickets((currentTickets) =>
        currentTickets.filter((ticketId) =>
          response.tickets.some(
            (ticket: FieldReportViewModel) => ticket.id === ticketId
          )
        )
      );
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const handleOnClose = () => {
    setAllTickets([]);
    setSelectedTickets([]);
    setSearchField("");
    setAssembler(undefined);
    onClose();
  };

  const getAssemblerTotalValue = (
    additions: FieldReportViewModel["PaymentAdditions"]
  ) => {
    let totalValue = 0;

    for (const add of additions) {
      //@ts-ignore
      if (add.status === "PENDING_APPROVEMENT") {
        totalValue = totalValue + add.value;
      }
    }

    return totalValue;
  };

  const handleBulkApprove = async () => {
    setLoading(true);

    try {
      await api(token).post(`/payment/additions/bulk-approve`, {
        fieldReportIds: selectedTickets,
      });
      handleSearch();
      setSelectedTickets([]);
      refetch();
      toast.success("Adicionais aprovados com sucesso.");
    } catch (error) {
      toast.error("Ocorreu um erro ao aprovar os adicionais.");
    } finally {
      setLoading(false);
    }
  };

  const handleSelectTicket = (id: number) => {
    setSelectedTickets((currentTickets) =>
      currentTickets.includes(id)
        ? currentTickets.filter((existingId) => existingId !== id)
        : [...currentTickets, id]
    );
  };

  const ticketIds = allTickets.map((ticket) => ticket.id);
  const selectedVisibleTickets = ticketIds.filter((ticketId) =>
    selectedTickets.includes(ticketId)
  );
  const areAllTicketsSelected =
    ticketIds.length > 0 && selectedVisibleTickets.length === ticketIds.length;
  const areSomeTicketsSelected = selectedVisibleTickets.length > 0;

  const handleSelectAllTickets = () => {
    if (!ticketIds.length) {
      return;
    }

    if (areAllTicketsSelected) {
      setSelectedTickets((currentTickets) =>
        currentTickets.filter((ticketId) => !ticketIds.includes(ticketId))
      );
      return;
    }

    setSelectedTickets((currentTickets) =>
      Array.from(new Set([...currentTickets, ...ticketIds]))
    );
  };

  return (
    <>
      <Modal
        width={1000}
        title={`Aprovação de Adicionais em Massa`}
        open={isOpen}
        footer={null}
        onCancel={handleOnClose}
        style={{ overflowY: "auto" }}
      >
        <div className="flex flex-col gap-4 py-2">
          <div className="flex gap-4">
            <div className="flex flex-col">
              <SearchAssemblers
                assembler={assembler}
                setAssembler={setAssembler}
              />
            </div>

            <Input
              value={searchField}
              onChange={(e) => setSearchField(e.target.value)}
              placeholder="Busca..."
            />
            <div className="flex items-end">
              <Button loading={loading} onClick={handleSearch}>
                Buscar Tickets
              </Button>
            </div>
          </div>

          <div className="flex flex-col">
            <div className="additions-approvement-table-header">
              <span>Tickets</span>
              <Checkbox
                checked={areAllTicketsSelected}
                disabled={!ticketIds.length}
                indeterminate={
                  areSomeTicketsSelected && !areAllTicketsSelected
                }
                onChange={handleSelectAllTickets}
              />
            </div>
            <Collapse
              style={{ color: "black" }}
              items={allTickets.map?.((ticket: FieldReportViewModel) => ({
                key: ticket.id,
                label: (
                  <div className="flex w-full justify-between">
                    {truncateString(ticket.title, 65)} - Total dos Adicionais:
                    R$
                    {getAssemblerTotalValue(ticket.PaymentAdditions).toFixed(2)}
                    <Checkbox
                      checked={
                        !!selectedTickets.find(
                          (existingId) => existingId === ticket.id
                        )
                      }
                      onClick={(e) => {
                        handleSelectTicket(ticket.id);

                        e.stopPropagation();
                      }}
                    />
                  </div>
                ),
                children: (
                  <>
                    <p>{ticket.notes}</p>
                    <p><b>Montador:</b>{` ${ticket.owner?.fullName ?? ''}`}</p>
                    <p><b>Responsável pelo agendamento:</b>{` ${ticket.history[0]?.user?.fullName ?? ''}`}</p>

                    <p><b>Valor do ticket sem adicionais:</b> {formatCurrency(ticket.products.reduce((acc, product) => acc = acc + product.value, 0))}</p>

                    <a href={`/montagem?id=${ticket.id}`}>Acessar Ticket</a>
                    <Table
                      bordered
                      dataSource={ticket.PaymentAdditions.map((add: any) => ({
                        ...add,
                      }))}
                      rowKey="id"
                      pagination={false}
                    >
                      <Table.Column
                        title="Descrição"
                        dataIndex="description"
                        key="title"
                      />
                      <Table.Column
                        title="Valor"
                        dataIndex={["value"]}
                        render={(value) => `${formatCurrency(value)}`}
                        key="assemblyTime"
                      />
                    </Table>
                    <div className="flex  text-md mt-6 justify-center font-bold "></div>
                  </>
                ),
              }))}
            />
            <div className="flex flex-col gap-10 mt-4">
              <div className="flex justify-between">
                <Button onClick={handleOnClose}>Cancelar</Button>
                <Button
                  loading={loading}
                  variant="solid"
                  color="primary"
                  onClick={handleBulkApprove}
                  disabled={!userHasPermission(user, {payments: ModulePermission.ADMIN}) || !selectedTickets.length}
                >
                  Aprovar
                </Button>
              </div>
            </div>
          </div>
        </div>
      </Modal>
      {/* <Button
        size="small"
        disabled={!hasMinimumPermissions("payments", user)}
        onClick={onOpen}
      >
        Efetuar Pagamento
      </Button> */}
    </>
  );
};
